import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, catchError, map } from 'rxjs';

export interface CountryInfo {
    country: string;
    countryCode: string;
    source: string;
}

@Injectable({
    providedIn: 'root'
})
export class CountryDetectionService {

    constructor(private http: HttpClient) { }

    /**
     * Main method to detect country with fallback chain
     */
    async detectCountry(): Promise<CountryInfo | null> {
        // Try methods in order of preference
        const methods = [
            () => this.detectFromCloudflare(),
            () => this.detectFromIPAPI(),
            () => this.detectFromIPInfo(),
            () => this.detectFromTimezone()
        ];

        for (const method of methods) {
            try {
                const result = await method().toPromise();
                if (result) {
                    return result;
                }
            } catch (error) {
                console.warn('Country detection method failed:', error);
            }
        }

        return null;
    }

    /**
     * Detect from Cloudflare headers (fastest, but requires Cloudflare)
     */
    private detectFromCloudflare(): Observable<CountryInfo | null> {
        return this.http.get('/cdn-cgi/trace', { responseType: 'text' }).pipe(
            map(response => {
                const lines = response.split('\n');
                const locLine = lines.find(line => line.startsWith('loc='));
                if (locLine) {
                    const countryCode = locLine.split('=')[1];
                    return {
                        country: this.getCountryName(countryCode),
                        countryCode: countryCode,
                        source: 'cloudflare'
                    };
                }
                return null;
            }),
            catchError(() => of(null))
        );
    }

    /**
     * Detect from ipapi.co (free tier: 1000 requests/day)
     */
    private detectFromIPAPI(): Observable<CountryInfo | null> {
        return this.http.get<any>('https://ipapi.co/json/').pipe(
            map(response => {
                if (response.country_code) {
                    return {
                        country: response.country_name,
                        countryCode: response.country_code,
                        source: 'ipapi'
                    };
                }
                return null;
            }),
            catchError(() => of(null))
        );
    }

    /**
     * Detect from ipinfo.io (free tier: 50k requests/month)
     */
    private detectFromIPInfo(): Observable<CountryInfo | null> {
        return this.http.get<any>('https://ipinfo.io/json').pipe(
            map(response => {
                if (response.country) {
                    return {
                        country: this.getCountryName(response.country),
                        countryCode: response.country,
                        source: 'ipinfo'
                    };
                }
                return null;
            }),
            catchError(() => of(null))
        );
    }

    /**
     * Detect from browser timezone (least accurate but always works)
     */
    private detectFromTimezone(): Observable<CountryInfo | null> {
        try {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const countryCode = this.timezoneToCountry(timezone);

            if (countryCode) {
                return of({
                    country: this.getCountryName(countryCode),
                    countryCode: countryCode,
                    source: 'timezone'
                });
            }
        } catch (error) {
            console.warn('Timezone detection failed:', error);
        }

        return of(null);
    }

    /**
     * Map timezone to country code (simplified mapping)
     */
    private timezoneToCountry(timezone: string): string | null {
        const timezoneMap: { [key: string]: string } = {
            'America/New_York': 'US',
            'America/Los_Angeles': 'US',
            'America/Chicago': 'US',
            'America/Denver': 'US',
            'Europe/London': 'GB',
            'Europe/Paris': 'FR',
            'Europe/Berlin': 'DE',
            'Europe/Madrid': 'ES',
            'Europe/Rome': 'IT',
            'Europe/Amsterdam': 'NL',
            'Europe/Warsaw': 'PL',
            'Europe/Prague': 'CZ',
            'Europe/Vienna': 'AT',
            'Europe/Zurich': 'CH',
            'Europe/Stockholm': 'SE',
            'Europe/Oslo': 'NO',
            'Europe/Copenhagen': 'DK',
            'Europe/Helsinki': 'FI',
            'Asia/Tokyo': 'JP',
            'Asia/Shanghai': 'CN',
            'Asia/Seoul': 'KR',
            'Asia/Kolkata': 'IN',
            'Asia/Dubai': 'AE',
            'Australia/Sydney': 'AU',
            'Australia/Melbourne': 'AU',
            'America/Toronto': 'CA',
            'America/Vancouver': 'CA',
            'America/Sao_Paulo': 'BR',
            'America/Mexico_City': 'MX',
            'Africa/Cairo': 'EG',
            'Africa/Johannesburg': 'ZA'
        };

        // Direct match
        if (timezoneMap[timezone]) {
            return timezoneMap[timezone];
        }

        // Try to extract country from timezone format (e.g., Europe/Warsaw -> could be PL)
        const parts = timezone.split('/');
        if (parts.length >= 2) {
            const region = parts[0];
            const city = parts[1];

            // Basic continent to country mapping for major cities
            if (region === 'Europe') {
                const europeanCities: { [key: string]: string } = {
                    'London': 'GB', 'Paris': 'FR', 'Berlin': 'DE', 'Madrid': 'ES',
                    'Rome': 'IT', 'Amsterdam': 'NL', 'Warsaw': 'PL', 'Prague': 'CZ',
                    'Vienna': 'AT', 'Zurich': 'CH', 'Stockholm': 'SE', 'Oslo': 'NO',
                    'Copenhagen': 'DK', 'Helsinki': 'FI', 'Dublin': 'IE', 'Brussels': 'BE'
                };
                return europeanCities[city] || null;
            }
        }

        return null;
    }

    /**
     * Get country name from country code
     */
    private getCountryName(countryCode: string): string {
        const countryNames: { [key: string]: string } = {
            'US': 'United States',
            'GB': 'United Kingdom',
            'FR': 'France',
            'DE': 'Germany',
            'ES': 'Spain',
            'IT': 'Italy',
            'NL': 'Netherlands',
            'PL': 'Poland',
            'CZ': 'Czech Republic',
            'AT': 'Austria',
            'CH': 'Switzerland',
            'SE': 'Sweden',
            'NO': 'Norway',
            'DK': 'Denmark',
            'FI': 'Finland',
            'IE': 'Ireland',
            'BE': 'Belgium',
            'JP': 'Japan',
            'CN': 'China',
            'KR': 'South Korea',
            'IN': 'India',
            'AE': 'United Arab Emirates',
            'AU': 'Australia',
            'CA': 'Canada',
            'BR': 'Brazil',
            'MX': 'Mexico',
            'EG': 'Egypt',
            'ZA': 'South Africa'
        };

        return countryNames[countryCode] || countryCode;
    }
}